import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DiversityForm } from "@/components/DiversityForm";
import { toast } from "sonner";
import { Upload, FileText, Sparkles, Paperclip } from "lucide-react";

export const ResumePage = () => {
  const [form, setForm] = useState({
    full_name: "", headline: "", skills: "", years_experience: "",
    raw_text: "", file_id: null, original_filename: null,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/resume/me").then((r) => {
      if (r.data) {
        setForm({
          full_name: r.data.full_name || "",
          headline: r.data.headline || "",
          skills: (r.data.skills || []).join(", "),
          years_experience: r.data.years_experience ?? "",
          raw_text: r.data.raw_text || "",
          file_id: r.data.file_id || null,
          original_filename: r.data.original_filename || null,
        });
      }
    }).catch(() => {});
  };
  useEffect(load, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        raw_text: data.raw_text,
        file_id: data.file_id,
        original_filename: data.original_filename,
      }));
      toast.success("Resume uploaded — text extracted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.raw_text.trim()) return toast.error("Please paste or upload your resume text");
    setSaving(true);
    try {
      await api.put("/resume", {
        full_name: form.full_name || null,
        headline: form.headline || null,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        raw_text: form.raw_text,
        file_id: form.file_id,
        original_filename: form.original_filename,
      });
      toast.success("Resume saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const autoExtract = async () => {
    if (!form.raw_text.trim()) return toast.error("Add resume text first");
    setExtracting(true);
    try {
      // Persist current text so extract endpoint sees it
      await api.put("/resume", {
        full_name: form.full_name || null,
        headline: form.headline || null,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        raw_text: form.raw_text,
        file_id: form.file_id,
        original_filename: form.original_filename,
      });
      const { data } = await api.post("/resume/extract");
      setForm((f) => ({
        ...f,
        full_name: data.full_name || f.full_name,
        headline: data.headline || f.headline,
        skills: (data.skills || []).join(", "),
        years_experience: data.years_experience ?? f.years_experience,
      }));
      toast.success("Fields auto-filled by AI");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI extraction failed");
    } finally { setExtracting(false); }
  };

  const downloadOriginal = async () => {
    if (!form.file_id) return;
    try {
      const res = await api.get(`/files/${form.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = form.original_filename || "resume";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error("Could not download file");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="mono-label">your resume</div>
      <h1 className="font-display text-3xl font-semibold tracking-tight mt-2">Tell Axiony about yourself</h1>
      <p className="text-slate-400 mt-2">The more detail, the sharper your match scores.</p>

      <div className="mt-8 border border-white/10 bg-[#111827] rounded-xl p-6 space-y-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-white/5 pb-4">
          <div>
            <div className="mono-label">ai assist</div>
            <div className="text-sm text-slate-300 mt-1">Let Gemini extract your headline, skills, and years of experience from your resume text.</div>
          </div>
          <Button
            type="button"
            onClick={autoExtract}
            disabled={extracting || !form.raw_text.trim()}
            data-testid="resume-autofill-btn"
            className="bg-[#06B6D4] hover:bg-[#0891B2] text-[#0B0F19] rounded-full font-medium"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {extracting ? "Extracting…" : "Auto-extract fields"}
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Full name</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                   data-testid="resume-name-input" className="bg-[#0B0F19] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Years of experience</Label>
            <Input type="number" min={0} step={0.5} value={form.years_experience}
                   onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                   data-testid="resume-years-input" className="bg-[#0B0F19] border-white/10" />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Headline</Label>
          <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })}
                 placeholder="e.g. Senior Full-Stack Engineer, ex-Stripe"
                 data-testid="resume-headline-input" className="bg-[#0B0F19] border-white/10" />
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Skills (comma separated)</Label>
          <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })}
                 placeholder="React, TypeScript, PostgreSQL, AWS"
                 data-testid="resume-skills-input" className="bg-[#0B0F19] border-white/10" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="text-slate-300">Resume text</Label>
            <div className="flex items-center gap-2">
              {form.file_id && (
                <button
                  type="button"
                  onClick={downloadOriginal}
                  data-testid="resume-download-original"
                  className="text-xs text-[#06B6D4] hover:underline flex items-center gap-1"
                >
                  <Paperclip className="w-3 h-3" />
                  {form.original_filename || "download"}
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,.txt"
                onChange={onUpload}
                className="hidden"
                id="resume-file-input"
                data-testid="resume-file-input"
              />
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="resume-upload-btn"
                className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white"
              >
                <Upload className="w-3.5 h-3.5 mr-2" />
                {uploading ? "Extracting…" : "Upload PDF/DOCX"}
              </Button>
            </div>
          </div>
          <Textarea
            rows={14}
            value={form.raw_text}
            onChange={(e) => setForm({ ...form, raw_text: e.target.value })}
            placeholder="Paste your full resume text here..."
            data-testid="resume-text-input"
            className="bg-[#0B0F19] border-white/10 font-mono text-xs leading-relaxed"
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="resume-save-btn" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-6">
            {saving ? "Saving…" : "Save resume"}
          </Button>
        </div>
      </div>

      <div className="mt-6 text-xs text-slate-500 flex items-center gap-2">
        <FileText className="w-3.5 h-3.5" />
        Stored securely. Recruiters can only view your resume when you apply to their role.
      </div>

      <DiversityForm />
    </div>
  );
};
